/* eslint-disable no-classname/no-classname-outside-designsystem */
import { Button } from 'designSystem/components/Button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'designSystem/components/Popover'
import { cn } from 'designSystem/lib/utils'
import { categorizeFfsLayers } from 'features/libra/workspacePanels/ffsLayerCategories'
import { ChevronDown, Eye, EyeOff, Layers } from 'lucide-react'
import { FC, useMemo, useState } from 'react'
import { GeckoMarkupLayer } from 'types'

interface FfsLayerVisibilityControlProps {
  /** FFS markup layers to list (indications -- with folded flaw boundaries -- and CTPs). */
  layers: ReadonlyArray<GeckoMarkupLayer>
  /** Layer ids currently hidden from the viewer. */
  hiddenLayerIds: ReadonlySet<string>
  /** Layer ids currently hovered (highlighted on the model + in the panel). */
  hoveredLayerIds: ReadonlySet<string>
  /** Bulk show/hide the given layer ids (`hidden=true` hides them). */
  onSetLayersHidden: (layerIds: string[], hidden: boolean) => void
  /** Set (or clear, with null) the hovered layer ids. */
  onHoverLayer: (layerIds: string[] | null) => void
  /** Frame the camera on a markup layer when its row is clicked. */
  onZoomToLayer: (layer: GeckoMarkupLayer) => void
}

/**
 * Floating, collapsible layer-visibility control overlaid on the FFS asset viz.
 *
 * Each row is an LTA (its indication, with the 1:1 flaw-boundary folded in) or a
 * CTP; toggling / hovering / zooming a row acts on all the layers it owns.
 * Rulers are excluded (global `sceneConfig.showRulers` toggle).
 *
 * Fully controlled/presentational: all state lives in `FfsAssetVizPanel` and is
 * passed in as props. Row grouping lives in `ffsLayerCategories.ts`.
 *
 * Styling note: this is an asset-viz OVERLAY, so it intentionally uses Tailwind
 * + the designSystem `Button`/`Popover` (not styled-components like most of
 * Libra) to match the viewer's native floating controls -- the same "glass"
 * recipe as `MeasurementStrip.tsx` and the 2D/3D toggle in
 * `ForecastAssetVizWindowSurface.tsx`. The `*_CLASS_NAME` constants hoist the
 * long/reused class strings, mirroring `MeasurementStrip`'s `MEASUREMENT_*`
 * constants; short one-off classes stay inline. The eslint-disable at the top
 * is the repo's sanctioned pragma for Tailwind in feature files.
 */

// Glass pill trigger, matching the 2D/3D toggle group's recipe.
const TRIGGER_CLASS_NAME =
  'pointer-events-auto h-6 gap-1 border border-border/70 bg-background/80 px-2 text-[0.625rem] font-medium text-muted-foreground shadow-sm backdrop-blur-sm hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground'

// Glass popover surface, matching the color-scale popup.
const PANEL_CLASS_NAME =
  'z-[1001] flex max-h-[24rem] w-60 flex-col gap-2 overflow-y-auto rounded-md border border-border/70 bg-background/90 p-2 shadow-sm ring-0 backdrop-blur-sm'

const CATEGORY_LABEL_CLASS_NAME =
  'flex-1 truncate text-[0.625rem] font-medium uppercase tracking-wide text-muted-foreground/80'

const EYE_BUTTON_CLASS_NAME =
  'shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground'

const EyeToggleButton: FC<{
  visible: boolean
  title: string
  ariaLabel: string
  onClick: () => void
}> = ({ visible, title, ariaLabel, onClick }) => (
  <Button
    type='button'
    variant='ghost'
    size='icon-xs'
    title={title}
    aria-label={ariaLabel}
    className={EYE_BUTTON_CLASS_NAME}
    onClick={onClick}>
    {visible ? (
      <Eye className='size-2.5' aria-hidden />
    ) : (
      <EyeOff className='size-2.5' aria-hidden />
    )}
  </Button>
)

export const FfsLayerVisibilityControl: FC<FfsLayerVisibilityControlProps> = ({
  layers,
  hiddenLayerIds,
  hoveredLayerIds,
  onSetLayersHidden,
  onHoverLayer,
  onZoomToLayer,
}) => {
  const [open, setOpen] = useState(false)

  const categories = useMemo(() => categorizeFfsLayers(layers), [layers])

  if (categories.length === 0) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className={TRIGGER_CLASS_NAME}>
          <Layers className='size-3' aria-hidden />
          Layers
          <ChevronDown
            className={cn(
              'size-3 opacity-70 transition-transform',
              open && 'rotate-180'
            )}
            aria-hidden
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        side='bottom'
        sideOffset={6}
        // Zooming to a markup moves focus to the 3D canvas; keep the panel
        // open on focus-out so the user can zoom to several rows in a row.
        // Click-outside and Escape still close it.
        onFocusOutside={(e) => e.preventDefault()}
        className={PANEL_CLASS_NAME}>
        {categories.map((category) => {
          const categoryIds = category.rows.flatMap((r) => r.layerIds)
          const anyVisible = categoryIds.some((id) => !hiddenLayerIds.has(id))
          return (
            <div key={category.key} className='flex flex-col'>
              <div className='flex items-center gap-1 px-1 pb-0.5'>
                <span className={CATEGORY_LABEL_CLASS_NAME}>
                  {category.label}
                </span>
                <span className='text-[0.625rem] tabular-nums text-muted-foreground/60'>
                  {category.rows.length}
                </span>
                <EyeToggleButton
                  visible={anyVisible}
                  title={anyVisible ? 'Hide all' : 'Show all'}
                  ariaLabel={`${anyVisible ? 'Hide' : 'Show'} all ${category.label}`}
                  onClick={() => onSetLayersHidden(categoryIds, anyVisible)}
                />
              </div>
              {category.rows.map((row) => {
                const visible = row.layerIds.some(
                  (id) => !hiddenLayerIds.has(id)
                )
                const hovered = row.layerIds.some((id) =>
                  hoveredLayerIds.has(id)
                )
                return (
                  <div
                    key={row.key}
                    className={cn(
                      'flex h-6 items-center gap-1 rounded-sm pr-1',
                      hovered ? 'bg-muted' : 'hover:bg-muted'
                    )}
                    onMouseEnter={() => onHoverLayer(row.layerIds)}
                    onMouseLeave={() => onHoverLayer(null)}
                    onFocus={() => onHoverLayer(row.layerIds)}
                    onBlur={() => onHoverLayer(null)}>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      title='Zoom to markup'
                      aria-label={`Zoom to ${row.label}`}
                      disabled={!visible}
                      className='h-6 min-w-0 flex-1 justify-start gap-2 px-1 font-normal hover:bg-transparent active:bg-transparent'
                      onClick={() => onZoomToLayer(row.zoomLayer)}>
                      <span
                        className='inline-block h-3 w-0.5 shrink-0 rounded-full'
                        style={{
                          backgroundColor: row.color || 'currentColor',
                        }}
                      />
                      <span
                        className={cn(
                          'flex-1 truncate text-left text-[0.625rem]',
                          visible
                            ? 'text-muted-foreground'
                            : 'text-muted-foreground/40'
                        )}>
                        {row.label}
                      </span>
                    </Button>
                    <EyeToggleButton
                      visible={visible}
                      title={visible ? 'Hide' : 'Show'}
                      ariaLabel={`${visible ? 'Hide' : 'Show'} ${row.label}`}
                      onClick={() => onSetLayersHidden(row.layerIds, visible)}
                    />
                  </div>
                )
              })}
            </div>
          )
        })}
      </PopoverContent>
    </Popover>
  )
}
